/**
 * Transaction Export Component
 * Allows users to export transaction history as CSV or PDF
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { getExportSummary, downloadTransactionsCSV, downloadTransactionsPDF } from '../lib/api';
import { toast } from 'sonner';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { 
  Download, 
  FileText, 
  FileSpreadsheet, 
  Calendar as CalendarIcon,
  Loader2,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Wallet
} from 'lucide-react';
import { cn } from '../lib/utils';

const TransactionExport = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [transactionType, setTransactionType] = useState('all');
  const [currency, setCurrency] = useState('all');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Preset date ranges
  const presets = [
    { label: 'Last 7 days', getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
    { label: 'Last 30 days', getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
    { label: 'This month', getValue: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
    { label: 'Last month', getValue: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    }},
    { label: 'This year', getValue: () => ({ start: startOfYear(new Date()), end: new Date() }) },
    { label: 'All time', getValue: () => ({ start: null, end: null }) },
  ];

  const applyPreset = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
  };

  // Fetch summary when dates change
  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const startStr = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const endStr = endDate ? format(endDate, 'yyyy-MM-dd') : null;
        const response = await getExportSummary(startStr, endStr);
        setSummary(response.data);
      } catch (error) {
        // No transactions found is not an error
        if (error.response?.status !== 404) {
          console.error('Error fetching summary:', error);
        }
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the API call
    const timer = setTimeout(fetchSummary, 300);
    return () => clearTimeout(timer);
  }, [startDate, endDate]);

  const handleExport = async (fileFormat) => {
    setExporting(true);
    
    try {
      const startStr = startDate ? format(startDate, 'yyyy-MM-dd') : null;
      const endStr = endDate ? format(endDate, 'yyyy-MM-dd') : null;
      
      let url;
      if (fileFormat === 'csv') {
        url = downloadTransactionsCSV(startStr, endStr, transactionType, currency);
      } else {
        url = downloadTransactionsPDF(startStr, endStr, transactionType, currency);
      }
      
      // Open download in new tab (will trigger browser download)
      window.open(url, '_blank');
      toast.success(`Downloading ${fileFormat.toUpperCase()} export...`);
    } catch (error) {
      toast.error(`Failed to export ${fileFormat.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'send': return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'receive': return <ArrowDownLeft className="w-4 h-4 text-emerald-500" />;
      case 'deposit': return <Wallet className="w-4 h-4 text-blue-500" />;
      case 'exchange': return <RefreshCw className="w-4 h-4 text-purple-500" />;
      default: return null;
    }
  };

  return (
    <Card data-testid="transaction-export-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Download className="w-5 h-5 text-emerald-500" />
          Export Transactions
        </CardTitle>
        <CardDescription>Download your transaction history as CSV or PDF</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Date Range Selection */}
        <div className="space-y-3">
          <Label>Date Range</Label>
          
          {/* Preset buttons */}
          <div className="flex flex-wrap gap-2">
            {presets.map((preset, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => applyPreset(preset)}
                className="text-xs"
                data-testid={`preset-${preset.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Custom date pickers */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                    data-testid="start-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                    data-testid="end-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Transaction Type</Label>
            <Select value={transactionType} onValueChange={setTransactionType}>
              <SelectTrigger data-testid="type-filter">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="deposit">Deposits</SelectItem>
                <SelectItem value="send">Sent</SelectItem>
                <SelectItem value="receive">Received</SelectItem>
                <SelectItem value="exchange">Exchanges</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="currency-filter">
                <SelectValue placeholder="All currencies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Currencies</SelectItem>
                {summary?.currencies?.map((curr) => (
                  <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Preview */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        ) : summary ? (
          <div className="space-y-3 p-4 rounded-xl bg-muted">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Export Preview</span>
              <Badge variant="secondary">{summary.total_transactions} transactions</Badge>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(summary.counts).map(([type, count]) => (
                count > 0 && (
                  <div key={type} className="flex items-center gap-2 text-sm">
                    {getTypeIcon(type)}
                    <span className="capitalize">{type}:</span>
                    <span className="font-medium">{count}</span>
                  </div>
                )
              ))}
            </div>

            <div className="pt-2 border-t border-border/50">
              <div className="text-xs text-muted-foreground">
                Currencies: {summary.currencies?.join(', ') || 'USD'}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No transactions found for this period</p>
          </div>
        )}

        {/* Export Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={() => handleExport('csv')}
            disabled={!summary || summary.total_transactions === 0 || exporting}
            variant="outline"
            className="flex-1 rounded-xl"
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
          
          <Button
            onClick={() => handleExport('pdf')}
            disabled={!summary || summary.total_transactions === 0 || exporting}
            className="flex-1 rounded-xl"
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransactionExport;
